import React, { useCallback, useEffect, useRef, useState } from "react";
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/20/solid'
import classNames from "../../utils/class-names";

export interface DropdownSelectorProps<T, K extends React.Key> {
    items: T[];
    onSelectionChanged: (selectedItem: T | null) => void;

    preSelectedItem?: T | null;
    selectedItem?: T | null;

    disabled?: boolean;

    keySelector?: (item: T) => K;
    displaySelector?: (item: T) => string;

    placeholder?: string;
    showClearButton?: boolean;

    selectedItemPrefix?: string;

    className?: string;
}

const DropdownSelector = <T, K extends React.Key>({
    items,
    selectedItem,
    disabled = false,
    onSelectionChanged,
    keySelector,
    displaySelector,
    placeholder = 'Sélectionner un élément',
    showClearButton = false,
    selectedItemPrefix = '',
    className,
    preSelectedItem = null,
}: DropdownSelectorProps<T, K>) => {

    const [internalSelectedItem, setInternalSelectedItem] = useState<T | null>(preSelectedItem);

    const isControlled = selectedItem !== undefined;
    const currentSelectedItem = isControlled ? selectedItem : internalSelectedItem;

    const handleSelectionChange = (item: T | null) => {
        if (!isControlled) {
            setInternalSelectedItem(item);
        }

        if (onSelectionChanged) {
            onSelectionChanged(item);
        }
    };

    const handleClearClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelectionChanged(null);
    };

    const getKey = useCallback((item: T): React.Key => {
        if (keySelector) {
            return keySelector(item);
        }

        return item as React.Key;
    }, [keySelector]);

    const getDisplay = (item: T): string => {
        if (displaySelector) {
            return displaySelector(item);
        }

        return String(item);
    };

    // Évite le problème de changement d'élément pré-sélectionné lors du premier rendu.
    const isFirstRender = useRef(true);

    // Gestion changement d'items dans la liste cas avec preselected item.
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (!isControlled && internalSelectedItem !== null) {
            const newSelection = items.length > 0 ? items[0] : null;
            setInternalSelectedItem(newSelection);

            if (onSelectionChanged) {
                onSelectionChanged(newSelection);
            }
        }
    }, [items, isControlled, onSelectionChanged, internalSelectedItem]);

    return (
        <Listbox value={currentSelectedItem} onChange={handleSelectionChange} disabled={disabled}>
            <div className={classNames(className ?? '', "flex w-full")}>
                <div className="relative w-full">
                    <ListboxButton
                        className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:outline-hidden focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:text-gray-300 disabled:ring-gray-200"
                    >
                        <span aria-disabled={disabled}
                            className={classNames(currentSelectedItem ? '' : "text-gray-400", "block truncate")}>
                            {currentSelectedItem ? `${selectedItemPrefix}${getDisplay(currentSelectedItem)}` : placeholder}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                        </span>
                    </ListboxButton>

                    <ListboxOptions
                        className="absolute z-20 mt-1 max-h-60 min-w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-inset ring-gray-300 ring-opacity-5 focus:outline-hidden sm:text-sm">
                        {items.map((item) => (
                            <ListboxOption
                                key={getKey(item)}
                                className={({ focus }) =>
                                    classNames(
                                        focus ? 'bg-indigo-600 text-white' : 'text-gray-900',
                                        'relative cursor-default select-none py-2 pl-3 pr-9'
                                    )
                                }
                                value={item}
                            >
                                {({ selected, focus }) => (
                                    <>
                                        <span
                                            className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                                            {getDisplay(item)}
                                        </span>
                                        {selected ? (
                                            <span
                                                className={classNames(
                                                    focus ? 'text-white' : 'text-indigo-600',
                                                    'absolute inset-y-0 right-0 flex items-center pr-4'
                                                )}
                                            >
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                        ) : null}
                                    </>
                                )}
                            </ListboxOption>
                        ))}
                    </ListboxOptions>
                </div>

                {showClearButton && currentSelectedItem && (
                    <button
                        onClick={handleClearClick}
                        type="button"
                        className="-ml-12 inset-y-0 items-center z-20"
                    >
                        <XMarkIcon className="h-5 w-5 text-red-700" aria-hidden="true" />
                    </button>
                )}
            </div>
        </Listbox>
    );
};

export default DropdownSelector;